import { useCallback, useEffect, useRef } from 'react';
import Animated from 'react-native-reanimated';
import type { NativeScrollEvent, NativeSyntheticEvent } from 'react-native';
import { useSafeTimeout, useWebDragToScroll } from '../../../../hooks';
import type { BannerScrollViewProps } from './types';

// web onScroll 정착 감지 디바운스(ms)
const WEB_SCROLL_SETTLE_MS = 120;

// web 구현: onMomentumScrollEnd 가 발화하지 않아 onScroll 을 디바운스해 정착을 감지한다.
//
// "한 번에 한 장" 은 마우스·터치 **같은 경로**로 보장한다 — `useWebDragToScroll` 이 팬을 직접
// 처리하며 드래그 도중 시작 페이지 ±1 로 클램프하고, 손을 뗄 때 속도 판정으로 착지 페이지를
// 정한다. 자세한 근거는 그 훅의 주석 참고.
//
// ⚠️ CSS 로는 이걸 만들 수 없어서 `pagingEnabled` 를 쓰지 않는다. `pagingEnabled` 는 RN Web 에서
// `scroll-snap-type: x mandatory` 로 매핑되는데, scroll-snap 은 **손을 뗀 뒤 정렬만** 하고
// 드래그 자체는 얼마든지 허용한다(`scroll-snap-stop: always` 도 관성 구간에만 관여). 그래서
// 한 제스처로 여러 장이 넘어갔다. 네이티브 `pagingEnabled` 에 대응하는 CSS 가 없다.
//
// (`snapToInterval` / `disableIntervalMomentum` 도 react-native-web 이 무시한다)
export const BannerScrollView = ({
  scrollRef,
  pageWidth,
  onSettle,
  onDragStart,
  onDragEnd,
  parallax = false,
  scrollX,
  contentContainerStyle,
  children,
}: BannerScrollViewProps) => {
  const setTimeout = useSafeTimeout();
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // 이번 제스처(터치/마우스 드래그)가 시작된 페이지. null 이면 프로그램적 스크롤(clone 점프·
  // autoScroll)로 보고 제한하지 않는다.
  const gestureStartIndexRef = useRef<number | null>(null);
  const lastOffsetRef = useRef(0);

  // 제스처 시작 시점의 페이지를 기준으로 삼는다. 무한 clone 점프나 autoScroll 처럼
  // 제스처 없이 일어나는 이동은 기준이 없어 그대로 통과 → 루프 보정을 방해하지 않는다.
  const markGestureStart = useCallback(() => {
    if (pageWidth <= 0) return;
    gestureStartIndexRef.current = Math.round(
      lastOffsetRef.current / pageWidth,
    );
  }, [pageWidth]);

  // 드래그(마우스·터치 공통): 시작 페이지 기록 + 자동 롤링 정지(소비 측 onDragStart).
  const handleDragStart = useCallback(() => {
    markGestureStart();
    onDragStart?.();
  }, [markGestureStart, onDragStart]);

  useWebDragToScroll(scrollRef, {
    // pageWidth 를 주면 페이징 모드 — 드래그 중 ±1 클램프 + 터치 팬 인수 + 속도 판정.
    pageWidth,
    // 배너는 "한 번에 한 장"이 계약이다. 휠/트랙패드 가로 스크롤은 제스처 시작점이 없어
    // 한 장 제한을 걸 수 없으므로 아예 막는다(세로 휠 = 페이지 스크롤은 통과).
    blockHorizontalWheel: true,
    onDragStart: handleDragStart,
    onDragEnd,
  });

  /** 착지 지점을 제스처 시작 페이지 ±1 로 제한하고 페이지 경계에 정렬한다. */
  const clampToOnePage = useCallback(
    (offsetX: number, startIndex: number | null) => {
      if (startIndex === null || pageWidth <= 0) return offsetX;
      const landedIndex = Math.round(offsetX / pageWidth);
      const limitedIndex = Math.min(
        startIndex + 1,
        Math.max(startIndex - 1, landedIndex),
      );
      return limitedIndex * pageWidth;
    },
    [pageWidth],
  );

  const handleScroll = useCallback(
    (e: NativeSyntheticEvent<NativeScrollEvent>) => {
      const offsetX = e.nativeEvent.contentOffset.x;
      // parallax: worklet scroll handler 대신 JS 에서 scrollX 갱신(web reanimated scroll
      // handler 는 신뢰 불가). native 와 동일한 파랄라스 효과 유지.
      if (parallax && scrollX) scrollX.value = offsetX;

      lastOffsetRef.current = offsetX;

      if (debounceRef.current) clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(() => {
        debounceRef.current = null;
        // 제스처로 시작된 이동만 한 페이지로 제한하고, 기준은 소비 후 비운다.
        const startIndex = gestureStartIndexRef.current;
        gestureStartIndexRef.current = null;
        // 훅이 이미 페이지 경계로 애니메이션해 착지시켰으므로 여기 클램프는 보통 no-op 이다.
        // 프로그램적 스크롤(clone 점프)은 startIndex 가 null 이라 그대로 통과한다.
        onSettle(clampToOnePage(offsetX, startIndex), true);
      }, WEB_SCROLL_SETTLE_MS);
    },
    [onSettle, parallax, scrollX, setTimeout, clampToOnePage],
  );

  useEffect(
    () => () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    },
    [],
  );

  return (
    <Animated.ScrollView
      ref={scrollRef}
      horizontal
      showsHorizontalScrollIndicator={false}
      contentContainerStyle={contentContainerStyle}
      onScroll={handleScroll}
      scrollEventThrottle={16}
      // 드래그 시작·종료는 훅의 onDragStart / onDragEnd 로만 받는다. 가로 팬을 우리가
      // 가져갔으므로 RN Web 의 onScrollBeginDrag 는 가로 제스처에서 발화하지 않는다.
    >
      {children}
    </Animated.ScrollView>
  );
};
